import type { Tier } from '@/state/GameState';
import { CALIDRIS_ID } from '@/systems/calidris';

export type KonkurrentTier = 'A' | 'S' | 'D';

export type Konkurrent = {
  id: string;
  spillNavn: string;
  tier: KonkurrentTier;
  arketype: string;
  signaturSjangre: string[];
  dialogToner: string[];
};

export type KonkurrentRaw = {
  id?: string;
  spillNavn?: string;
  tier?: string;
  arketype?: string;
  signaturSjangre?: unknown;
  dialogToner?: unknown;
};

const SJANGER_SYNONYMER: Record<string, string[]> = {
  KRIM: ['krim', 'kioskkrim'],
  ROMANCE: ['romance', 'feelgood', 'serieromaner'],
  FEELGOOD: ['feelgood', 'serieromaner', 'lettlest'],
  LITERARY: ['litterær roman', 'skjønnlitteratur', 'høyverdig debut', 'klassikere'],
  POETRY: ['lyrikk'],
  NONFICTION: ['sakprosa', 'biografi', 'essay'],
  BARNEBOK: ['barnebok'],
  FANTASY: ['fantasy'],
};

export class KonkurrentKatalog {
  private _konkurrenter: Konkurrent[] = [];
  private _loaded = false;

  loadFromArray(raw: KonkurrentRaw[]): void {
    this._konkurrenter = raw
      .filter((k): k is KonkurrentRaw => !!k && typeof k.id === 'string')
      .filter(
        (k) =>
          typeof k.spillNavn === 'string' &&
          (k.tier === 'A' || k.tier === 'S' || k.tier === 'D'),
      )
      .map((k) => ({
        id: k.id as string,
        spillNavn: k.spillNavn as string,
        tier: k.tier as KonkurrentTier,
        arketype: typeof k.arketype === 'string' ? k.arketype : '',
        signaturSjangre: Array.isArray(k.signaturSjangre)
          ? (k.signaturSjangre as unknown[]).filter(
              (s): s is string => typeof s === 'string',
            )
          : [],
        dialogToner: Array.isArray(k.dialogToner)
          ? (k.dialogToner as unknown[]).filter(
              (s): s is string => typeof s === 'string',
            )
          : [],
      }));
    this._loaded = true;
  }

  get isReady(): boolean {
    return this._loaded;
  }

  get konkurrenter(): Konkurrent[] {
    return this._konkurrenter.map((k) => this.clone(k));
  }

  get vanligeKonkurrenter(): Konkurrent[] {
    return this._konkurrenter
      .filter((k) => k.id !== CALIDRIS_ID)
      .map((k) => this.clone(k));
  }

  findById(id: string): Konkurrent | undefined {
    const k = this._konkurrenter.find((x) => x.id === id);
    return k ? this.clone(k) : undefined;
  }

  private clone(k: Konkurrent): Konkurrent {
    return {
      ...k,
      signaturSjangre: [...k.signaturSjangre],
      dialogToner: [...k.dialogToner],
    };
  }
}

export function harSjangerMatch(konkurrent: Konkurrent, sjanger: string): boolean {
  const sjangerNorm = sjanger.trim().toUpperCase();
  const synonymer = SJANGER_SYNONYMER[sjangerNorm] ?? [sjangerNorm.toLowerCase()];
  const signaturNorm = konkurrent.signaturSjangre.map((s) => s.toLowerCase());
  for (const syn of synonymer) {
    if (signaturNorm.some((s) => s.includes(syn))) return true;
  }
  return false;
}

export function vektForKonkurrent(konkurrent: Konkurrent, sjanger: string): number {
  return harSjangerMatch(konkurrent, sjanger) ? 2 : 1;
}

export function velgKonkurrentForSjanger(
  liste: ReadonlyArray<Konkurrent>,
  sjanger: string,
  rng: () => number,
): Konkurrent | null {
  if (liste.length === 0) return null;
  const vekter = liste.map((k) => vektForKonkurrent(k, sjanger));
  const sum = vekter.reduce((a, b) => a + b, 0);
  if (sum <= 0) return liste[0];
  let n = rng() * sum;
  for (let i = 0; i < liste.length; i += 1) {
    n -= vekter[i];
    if (n <= 0) return liste[i];
  }
  return liste[liste.length - 1];
}

export function tilbudForTier(tier: Tier): number {
  switch (tier) {
    case 'D':
      return 80_000;
    case 'C':
      return 120_000;
    case 'B':
      return 160_000;
    case 'A':
      return 180_000;
    case 'S':
      return 200_000;
    default:
      return 80_000;
  }
}

export const konkurrentKatalog = new KonkurrentKatalog();
